import { Link } from 'react-router-dom';
import Divider from '../components/Divider';
import { Award, Users, Heart, Flame } from 'lucide-react';

const About = () => {
  return (
    <main className="pt-24 min-h-screen bg-white" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 px-6 text-center bg-gray-50">
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-4">
          ABOUT <span className="text-brand-red">US</span>
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Discover the story behind Butt Karahi Luton — where tradition meets excellence.
        </p>
      </section>

      <Divider className="py-6" />

      {/* Story Section */}
      <section className="py-16 px-6" data-testid="story-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-black mb-6">
                The Legacy of <span className="text-brand-red">Butt Karahi</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  The name of Butt Karahi speaks volumes for itself — an upscale restaurant that has become 
                  synonymous with authentic Pakistani cuisine across the United Kingdom. We serve scrumptious, 
                  hygienic and nutritious food packed with layers of exotic flavour.
                </p>
                <p>
                  Traditional, authentic recipes are followed with a little tweak of contemporary flavour 
                  to make each dish luscious and irresistible. Our expert, dedicated, courteous and hospitable 
                  team does its utmost to satisfy your craving for Pakistani cuisine.
                </p>
                <p>
                  As far as ambiance at Butt Karahi Luton is concerned, you experience a tranquil and serene 
                  environment after a blistering work day. We value our roots and promote Pakistani food culture 
                  through every dish we serve.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://buttkarahisouthall.co.uk/wp-content/uploads/2023/12/JAVSAN-6.jpg"
                alt="Butt Karahi signature dish"
                className="w-full border-4 border-brand-red/20"
              />
              <div className="absolute -bottom-6 -right-6 bg-brand-red text-white px-8 py-4 font-serif text-2xl font-bold">
                Est. Lahore
              </div>
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* Luton Branch Section */}
      <section className="py-16 px-6 bg-gray-50" data-testid="luton-branch-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://buttkarahisouthall.co.uk/wp-content/uploads/2023/12/JAVSAN-10.jpg"
                alt="Restaurant interior"
                className="w-full border-4 border-brand-red/20"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-black mb-6">
                Butt Karahi <span className="text-brand-red">Luton</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Butt Karahi Luton is proud to be part of the renowned Butt Karahi franchise, 
                  bringing the same commitment to quality and authenticity that has made the brand 
                  famous across Pakistan and the UK.
                </p>
                <p>
                  Located in the heart of Luton at 4 Cumberland Street, our restaurant offers 
                  a welcoming atmosphere where families and friends can gather to enjoy the finest 
                  Pakistani cuisine.
                </p>
                <p>
                  Whether you're craving our famous Karahi, succulent BBQ, or the legendary Lamb Sajji, 
                  our skilled chefs prepare each dish with passion and expertise, using only the freshest 
                  ingredients and traditional cooking methods.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link 
                  to="/menu"
                  className="bg-brand-red text-white px-8 py-3 font-semibold text-sm tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
                >
                  View Menu
                </Link>
                <Link 
                  to="/contact"
                  className="border-2 border-brand-red text-brand-red px-8 py-3 font-semibold text-sm tracking-wider uppercase hover:bg-brand-red hover:text-white transition-all duration-300"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* Values Section */}
      <section className="py-16 px-6 bg-white" data-testid="values-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center text-black mb-4">
            OUR <span className="text-brand-red">VALUES</span>
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
            What sets us apart and drives our commitment to excellence every single day.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-gray-200 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-all duration-300">
                <Award className="text-brand-red" size={36} />
              </div>
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Quality</h3>
              <p className="text-gray-500 text-sm">Only the finest ingredients make it to your plate.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-gray-200 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-all duration-300">
                <Flame className="text-brand-red" size={36} />
              </div>
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Authenticity</h3>
              <p className="text-gray-500 text-sm">Traditional recipes passed down through generations.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-gray-200 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-all duration-300">
                <Users className="text-brand-red" size={36} />
              </div>
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Hospitality</h3>
              <p className="text-gray-500 text-sm">Every guest is treated like family.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-gray-200 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-all duration-300">
                <Heart className="text-brand-red" size={36} />
              </div>
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Passion</h3>
              <p className="text-gray-500 text-sm">Love for Pakistani cuisine in every dish.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 px-6 bg-brand-red">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            Experience the Taste of Excellence
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Visit us at 4 Cumberland Street, Luton, or call to place your order. 
            We're open daily from 1PM to 11:30PM.
          </p>
          <a 
            href="tel:01582517786"
            className="inline-block bg-white text-brand-red px-10 py-4 font-semibold text-sm tracking-wider uppercase hover:bg-gray-100 transition-all duration-300"
          >
            Call 01582 517786
          </a>
        </div>
      </section>
    </main>
  );
};

export default About;
